<template>
	<div class="device-management-page" v-focus-section>
		<div class="page-header">
			<h1>{{ $t("devices.title") }}</h1>
			<p class="subtitle">{{ $t("devices.subtitle", { max: maxDevices }) }}</p>
		</div>

		<!-- Device Limit Warning -->
		<div v-if="showDeviceLimitWarning" class="device-limit-warning">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				stroke-linecap="round"
				stroke-linejoin="round"
			>
				<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
				<line x1="12" y1="9" x2="12" y2="13"></line>
				<line x1="12" y1="17" x2="12.01" y2="17"></line>
			</svg>
			<p>{{ $t("devices.deviceLimitReached") }}</p>
		</div>

		<!-- Loading State -->
		<div v-if="loading && devices.length === 0" class="loading-state">
			<pulse-loader :loading="true" :color="'white'" :size="'12px'"></pulse-loader>
		</div>

		<!-- Error State -->
		<div v-if="error" class="error-message">
			<p>{{ error }}</p>
		</div>

		<!-- Device List -->
		<div v-if="!loading || devices.length > 0" class="devices-container">
			<div v-if="devices.length === 0 && !loading" class="empty-state">
				<p>{{ $t("devices.noDevices") }}</p>
			</div>

			<div v-for="device in devices" :key="device.device_id" class="device-card">
				<div class="device-info">
					<div class="device-icon">
						<svg
							v-if="device.device_name.includes('iOS') || device.device_name.includes('Android')"
							xmlns="http://www.w3.org/2000/svg"
							height="24px"
							viewBox="0 -960 960 960"
							width="24px"
							fill="#FFFFFF"
						>
							<path
								d="M280-40q-33 0-56.5-23.5T200-120v-720q0-33 23.5-56.5T280-920h400q33 0 56.5 23.5T760-840v124q18 7 29 22t11 34v80q0 19-11 34t-29 22v404q0 33-23.5 56.5T680-40H280Zm0-80h400v-720H280v720Zm0 0v-720 720Zm120-40h160q17 0 28.5-11.5T600-200q0-17-11.5-28.5T560-240H400q-17 0-28.5 11.5T360-200q0 17 11.5 28.5T400-160Z"
							/>
						</svg>
						<svg
							v-else
							xmlns="http://www.w3.org/2000/svg"
							height="24px"
							viewBox="0 -960 960 960"
							width="24px"
							fill="#FFFFFF"
						>
							<path
								d="M40-120v-80h880v80H40Zm120-120q-33 0-56.5-23.5T80-320v-440q0-33 23.5-56.5T160-840h640q33 0 56.5 23.5T880-760v440q0 33-23.5 56.5T800-240H160Zm0-80h640v-440H160v440Zm0 0v-440 440Z"
							/>
						</svg>
					</div>
					<div class="device-details">
						<h3 class="device-name">{{ device.device_name }}</h3>
						<p class="device-id">{{ $t("devices.deviceId") }}: {{ truncateDeviceId(device.device_id) }}</p>
						<p class="device-meta">{{ $t("devices.lastActive") }}: {{ formatDate(device.last_active) }}</p>
						<p class="device-meta">{{ $t("devices.registered") }}: {{ formatDate(device.created) }}</p>
						<span v-if="isCurrentDevice(device.device_id)" class="current-device-badge">
							{{ $t("devices.currentDevice") }}
						</span>
					</div>
				</div>
				<div class="device-actions">
					<button
						@click="handleRemoveDevice(device.device_id)"
						:disabled="removingDevice === device.device_id"
						v-focus
						class="btn-remove"
					>
						<span v-if="removingDevice === device.device_id">
							<pulse-loader :loading="true" :color="'white'" :size="'8px'"></pulse-loader>
						</span>
						<span v-else>{{ $t("devices.remove") }}</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Register Current Device Button -->
		<div v-if="!isCurrentDeviceRegistered && devices.length < maxDevices" class="register-section" v-focus-section>
			<button @click="handleRegisterCurrentDevice" :disabled="loading" class="btn-register" v-focus>
				{{ $t("devices.registerThisDevice") }}
			</button>
		</div>

		<!-- Confirmation Dialog -->
		<Dialog
			v-model:visible="showRemoveDialog"
			:header="$t('devices.confirmRemoveTitle')"
			:modal="true"
			:closable="true"
			:style="{ width: '450px' }"
			:breakpoints="{ '960px': '75vw', '640px': '90vw' }"
			v-focus-section
			@show="onDialogShow"
		>
			<p>{{ $t("devices.confirmRemove") }}</p>
			<template #footer>
				<button class="btn-cancel" ref="cancelButton" @click="showRemoveDialog = false" v-focus>
					{{ $t("devices.cancel") }}
				</button>
				<button class="btn-confirm-remove" ref="confirmButton" @click="confirmRemoveDevice" v-focus>
					{{ $t("devices.confirmRemoveButton") }}
				</button>
			</template>
		</Dialog>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, computed } from "vue"
	import { useDeviceManager } from "../stores/deviceManager"
	import { useI18n } from "vue-i18n"
	import { useRoute } from "vue-router"
	import PulseLoader from "vue-spinner/src/PulseLoader.vue"
	import Dialog from "primevue/dialog"
	import { useRouter } from "vue-router"
	const router = useRouter()
	const { t } = useI18n()
	const route = useRoute()
	const deviceManager = useDeviceManager()
	const {
		maxDevices,
		loading,
		error,
		listDevices,
		deactivateDevice,
		getCurrentDeviceId,
		getDeviceName,
		registerDevice,
	} = deviceManager

	const devices = computed(() => deviceManager.registeredDevices)

	const removingDevice = ref<string | null>(null)
	const showRemoveDialog = ref(false)
	const deviceToRemove = ref<string | null>(null)
	const currentDeviceId = ref<string>("")
	const cancelButton = ref(null)
	const confirmButton = ref(null)

	// Get current device ID on mount
	onMounted(async () => {
		currentDeviceId.value = await getCurrentDeviceId()
		await listDevices()
	})

	// Focus the first button when the dialog is shown
	const onDialogShow = () => {
		setTimeout(() => {
			if (cancelButton.value) {
				cancelButton.value.focus()
			}
		}, 0)
	}

	// Handle keyboard navigation for Samsung TV remote
	const handleKeydown = (event: KeyboardEvent) => {
		if (showRemoveDialog.value) {
			if (event.key === "ArrowRight" && cancelButton.value && confirmButton.value) {
				confirmButton.value.focus()
			} else if (event.key === "ArrowLeft" && cancelButton.value && confirmButton.value) {
				cancelButton.value.focus()
			}
		}
	}

	// Add event listener for keydown
	window.addEventListener("keydown", handleKeydown)

	// Cleanup event listener when the component is unmounted
	import { onUnmounted } from "vue"
	onUnmounted(() => {
		window.removeEventListener("keydown", handleKeydown)
	})

	// Check if we should show the device limit warning
	const showDeviceLimitWarning = computed(() => {
		return route.query.error === "device_limit"
	})

	const isCurrentDeviceRegistered = computed(() => {
		return devices.value.some((device) => device.device_id === currentDeviceId.value)
	})

	const isCurrentDevice = (deviceId: string): boolean => {
		return deviceId === currentDeviceId.value
	}

	const truncateDeviceId = (deviceId: string): string => {
		if (deviceId.length <= 20) return deviceId
		return `${deviceId.substring(0, 10)}...${deviceId.substring(deviceId.length - 10)}`
	}

	const formatDate = (timestamp: number): string => {
		const date = new Date(timestamp * 1000)
		return new Intl.DateTimeFormat(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		}).format(date)
	}

	const handleRemoveDevice = (deviceId: string) => {
		deviceToRemove.value = deviceId
		showRemoveDialog.value = true
	}

	const confirmRemoveDevice = async () => {
		if (!deviceToRemove.value) return

		showRemoveDialog.value = false
		removingDevice.value = deviceToRemove.value
		const success = await deactivateDevice(deviceToRemove.value)

		if (success) {
			// If user removed the current device, clear the device ID from localStorage
			if (deviceToRemove.value === currentDeviceId.value) {
				localStorage.removeItem("device_id")
			}
		}

		removingDevice.value = null
		deviceToRemove.value = null
	}

	const handleRegisterCurrentDevice = async () => {
		const deviceName = getDeviceName()
		await registerDevice(currentDeviceId.value, deviceName)
		router.push({ name: "Home" })
	}
</script>

<style scoped lang="scss">
	.device-management-page {
		max-width: 1200px;
		margin: 0 auto;
		padding: 40px 20px;
		min-height: 100vh;

		@include br.tablet-down {
			padding: 20px 15px;
		}
	}

	.page-header {
		margin-bottom: 40px;

		h1 {
			font-size: 36px;
			font-weight: 700;
			margin-bottom: 10px;
			color: var(--white);

			@include br.tablet-down {
				font-size: 28px;
			}
		}

		.subtitle {
			font-size: 16px;
			color: var(--gray);
		}
	}

	.loading-state,
	.empty-state {
		text-align: center;
		padding: 60px 20px;
		color: var(--gray);
	}

	.error-message {
		background: rgba(255, 0, 0, 0.1);
		border: 1px solid rgba(255, 0, 0, 0.3);
		border-radius: 8px;
		padding: 20px;
		margin-bottom: 20px;
		color: #ff4444;
	}

	.device-limit-warning {
		background: rgba(255, 193, 7, 0.1);
		border: 1px solid rgba(255, 193, 7, 0.4);
		border-radius: 12px;
		padding: 20px;
		margin-bottom: 30px;
		display: flex;
		align-items: flex-start;
		gap: 15px;

		svg {
			color: #ffc107;
			flex-shrink: 0;
			margin-top: 2px;
		}

		p {
			color: #ffc107;
			font-size: 16px;
			line-height: 1.5;
			margin: 0;
		}

		@include br.mobile-down {
			padding: 16px;

			p {
				font-size: 14px;
			}
		}
	}

	.devices-container {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.device-card {
		background: rgba(255, 255, 255, 0.05);
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: 12px;
		padding: 24px;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		transition: all 0.3s ease;

		&:hover {
			background: rgba(255, 255, 255, 0.08);
			border-color: rgba(255, 255, 255, 0.2);
		}

		@include br.mobile-down {
			flex-direction: column;
			gap: 20px;
		}
	}

	.device-info {
		display: flex;
		gap: 20px;
		flex: 1;
	}

	.device-icon {
		width: 48px;
		height: 48px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		svg {
			color: var(--white);
		}
	}

	.device-details {
		flex: 1;

		.device-name {
			font-size: 20px;
			font-weight: 600;
			margin-bottom: 8px;
			color: var(--white);
		}

		.device-id {
			font-size: 14px;
			color: var(--gray);
			margin-bottom: 8px;
			font-family: monospace;
		}

		.device-meta {
			font-size: 14px;
			color: var(--gray);
			margin-bottom: 4px;
		}

		.current-device-badge {
			display: inline-block;
			background: rgba(0, 255, 0, 0.2);
			color: #00ff00;
			padding: 4px 12px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 600;
			margin-top: 8px;
		}
	}

	.device-actions {
		display: flex;
		align-items: center;

		@include br.mobile-down {
			width: 100%;
			justify-content: flex-end;
		}
	}

	.btn-remove {
		background: rgba(255, 0, 0, 0.2);
		color: #ff4444;
		border: 1px solid rgba(255, 0, 0, 0.3);
		padding: 10px 20px;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		min-width: 100px;

		&:hover:not(:disabled) {
			background: rgba(255, 0, 0, 0.3);
			border-color: rgba(255, 0, 0, 0.5);
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	.register-section {
		margin-top: 40px;
		text-align: center;
		padding: 30px;
		background: rgba(255, 255, 255, 0.03);
		border: 1px dashed rgba(255, 255, 255, 0.2);
		border-radius: 12px;
	}

	.btn-register {
		background: var(--primary-color, #007bff);
		color: var(--white);
		border: none;
		padding: 14px 32px;
		border-radius: 8px;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;

		&:hover:not(:disabled) {
			background: var(--primary-color-dark, #0056b3);
			transform: translateY(-2px);
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	// Dialog button styles
	.btn-cancel,
	.btn-confirm-remove {
		padding: 10px 24px;
		border-radius: 6px;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		border: none;
	}

	.btn-cancel {
		background: rgba(255, 255, 255, 0.1);
		color: var(--white);

		&:hover {
			background: rgba(255, 255, 255, 0.2);
		}
	}

	.btn-confirm-remove {
		background: var(--red);
		color: white;
		margin-left: 10px;

		&:hover {
			background: #c30811;
		}
	}
</style>
